import { Button, HStack, Stack, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { debounce, uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { OrganizationRoleType, PersonDto, useListPersonsQuery } from "../../autogen/bff-api";
import { dedupeByEmail } from "../contactPersons/supplierContactPersons";
import { normalizeEmail } from "../email/normalizeEmail";
import { displayPersonNameWithEmail } from "./displayPersonName";
import { MultiSelector } from "../input/Selector/MultiSelector";
import { SelectorOptions } from "../input/Selector/SelectorValue";

const ACCOUNTS_LIMIT = 100;

export const PersonsSelector = ({
  organizationIds,
  selectedPersons,
  placeholder,
  placeholderColor = "",
  onChange,
  personsToExclude = [],
  additionalPersons,
  size = "sm",
  roles,
  inputId,
  ariaLabel,
}: {
  organizationIds: string[] | null;
  selectedPersons?: PersonDto[];
  personsToExclude?: string[];
  additionalPersons?: PersonDto[];
  placeholder?: string;
  placeholderColor?: string;
  onChange: (persons: PersonDto[]) => void | Promise<void>;
  size?: "sm" | "md";
  roles?: OrganizationRoleType[];
  inputId?: string;
  ariaLabel?: string;
}) => {
  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setDebouncedSearchName(name), 300),
    []
  );

  const {
    data: personsRes,
    isFetching,
    isError: personsFailed,
    refetch: refetchPersons,
  } = useListPersonsQuery(
    organizationIds?.length
      ? {
          orgIds: organizationIds,
          name: debouncedSearchName ? debouncedSearchName : undefined,
          roles,
          limit: 10,
        }
      : skipToken
  );

  const {
    data: accountsRes,
    isFetching: isFetchingAccounts,
    isError: accountsFailed,
    refetch: refetchAccounts,
  } = useListPersonsQuery(
    organizationIds?.length && additionalPersons?.length
      ? { orgIds: organizationIds, roles, limit: ACCOUNTS_LIMIT }
      : skipToken
  );

  const options: SelectorOptions = useMemo(() => {
    const accountEmails = new Set(
      [...(accountsRes?.persons ?? []), ...(personsRes?.persons ?? [])].map((p) => normalizeEmail(p.email))
    );
    const selectedEmails = new Set((selectedPersons ?? []).map((p) => normalizeEmail(p.email)));
    const contactsWithoutAccount = (additionalPersons ?? []).filter(
      (p) => !accountEmails.has(normalizeEmail(p.email))
    );
    const allPersons = dedupeByEmail(
      uniqBy([...(personsRes?.persons ?? []), ...contactsWithoutAccount], (p) => p.id),
      (p) => p.email
    );
    return (
      allPersons
        .filter((p) => !personsToExclude.includes(p.id))
        .filter((o) => !selectedPersons?.map((sp) => sp.id).includes(o.id))
        .filter((p) => !selectedEmails.has(normalizeEmail(p.email)))
        .filter(
          (p) =>
            `${p.firstName} ${p.lastName}`.toLowerCase().includes(debouncedSearchName.toLowerCase()) ||
            `${p.lastName} ${p.firstName}`.toLowerCase().includes(debouncedSearchName.toLowerCase()) ||
            p.email.toLowerCase().includes(debouncedSearchName.toLowerCase())
        )
        .map((o) => ({ label: displayPersonNameWithEmail(o), value: o.id })) ?? []
    );
  }, [
    accountsRes?.persons,
    additionalPersons,
    personsRes?.persons,
    personsToExclude,
    debouncedSearchName,
    selectedPersons,
  ]);

  const loadFailed = personsFailed || accountsFailed;
  const retry = () => {
    if (personsFailed) refetchPersons();
    if (accountsFailed) refetchAccounts();
  };

  return (
    <Stack spacing="1" w="full">
      <MultiSelector
        inputId={inputId}
        ariaLabel={ariaLabel}
        size={size}
        placeholder={{ text: placeholder ?? `${t("Select person")}...`, color: placeholderColor }}
        value={
          selectedPersons?.map((p) => ({
            label: displayPersonNameWithEmail(p),
            value: p.id,
          })) ?? []
        }
        inputValue={searchName}
        isLoading={isFetching || isFetchingAccounts}
        onInputChange={(name: string) => {
          setSearchName(name);
          handleDebouncedSearchName(name);
        }}
        options={options}
        onChange={async (o) => {
          const selectedIds = o.map((o) => o.value);
          const selectablePersons = uniqBy(
            [...(selectedPersons ?? []), ...(personsRes?.persons ?? []), ...(additionalPersons ?? [])],
            (p) => p.id
          );
          const persons = selectablePersons.filter((p) => selectedIds.includes(p.id));
          if (o && selectedIds.length !== persons.length) throw Error("Illegal state: Persons not found");
          await onChange(persons ?? []);
        }}
        noOptionsAvailableMessage={loadFailed ? t("The people could not be loaded.") : t("No persons")}
      />
      {loadFailed && (
        <HStack spacing="2">
          <Text fontSize="sm" color="smError">
            {t("The people could not be loaded.")}
          </Text>
          <Button size="xs" variant="link" colorScheme="teal" onClick={retry} isLoading={isFetching || isFetchingAccounts}>
            {t("Try again")}
          </Button>
        </HStack>
      )}
    </Stack>
  );
};
